import { FC, FormEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  alpha,
  Box,
  BoxProps,
  IconButton,
  InputAdornment,
  inputAdornmentClasses,
  inputBaseClasses,
  styled,
  TextField,
} from '@mui/material';
import classNames from 'classnames';

import { Icon } from '../Icon';

const StyledSearchField = styled(TextField)`
  .${inputAdornmentClasses.root} {
    margin-top: 0 !important;
  }

  .${inputBaseClasses.root} {
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    padding-left: ${({ theme }) => theme.spacing()};
    padding-right: ${({ theme }) => theme.spacing()};
    font-size: ${({ theme }) => theme.typography.body2.fontSize};

    &::before,
    &::after {
      content: none;
    }
  }

  .${inputBaseClasses.input} {
    padding: ${({ theme }) => theme.spacing(0.5, 1.5, 0.5, 0)};
  }

  &.active {
    .${inputBaseClasses.root} {
      background: ${({ theme }) =>
        alpha(theme.palette.info.main, theme.palette.action.activatedOpacity)};
    }
  }
`;

type TableSearchFieldProps = {
  debounceMs?: number;
  /**
   * If not provided, submission value will be handled as a URL search param
   */
  onSubmit?: (value: string) => void;
  /**
   * If not provided, value will be retrieved from URL search params
   */
  value?: string;
  wrapperProps?: BoxProps<'form'>;
};

const TableSearchField: FC<TableSearchFieldProps> = ({
  debounceMs,
  onSubmit,
  value,
  wrapperProps,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const { t } = useTranslation();
  const externalValue = value ?? q;

  const [queryText, setQueryText] = useState(externalValue);
  const lastSubmittedValue = useRef(externalValue);
  const pendingSubmit = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const submitValue = useCallback(
    (nextValue: string) => {
      if (pendingSubmit.current) clearTimeout(pendingSubmit.current);
      pendingSubmit.current = undefined;
      lastSubmittedValue.current = nextValue;

      if (onSubmit) onSubmit(nextValue);
      else {
        setSearchParams(prevParams => {
          const newParams = new URLSearchParams(prevParams);
          if (nextValue) newParams.set('q', nextValue);
          else newParams.delete('q');
          return newParams;
        });
      }
    },
    [onSubmit, setSearchParams]
  );

  useEffect(() => {
    setQueryText(externalValue);
    lastSubmittedValue.current = externalValue;
  }, [externalValue]);

  useEffect(() => {
    if (!debounceMs || queryText === lastSubmittedValue.current) return;

    const timeout = setTimeout(() => submitValue(queryText), debounceMs);
    pendingSubmit.current = timeout;

    return () => {
      clearTimeout(timeout);
      if (pendingSubmit.current === timeout) pendingSubmit.current = undefined;
    };
  }, [debounceMs, queryText, submitValue]);

  const clear = () => {
    setQueryText('');
    submitValue('');
  };

  const submit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    e.stopPropagation();

    submitValue(queryText);
  };

  return (
    <Box component='form' flex={1} maxWidth={500} onSubmit={submit} {...wrapperProps}>
      <StyledSearchField
        autoComplete='off'
        className={classNames({ active: queryText })}
        color='info'
        fullWidth
        name='q'
        onChange={({ currentTarget: { value } }) => setQueryText(value)}
        placeholder={t('filter')}
        slotProps={{
          input: {
            'aria-label': t('filterList'),
            endAdornment:
              queryText ? (
                <InputAdornment position='end'>
                  <IconButton edge='end' onClick={clear} size='small'>
                    <Icon fontSize='small' name='cancel' />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            startAdornment: (
              <InputAdornment position='start'>
                <Icon fontSize='small' name='filterAlt' />
              </InputAdornment>
            ),
          },
        }}
        value={queryText}
        variant='filled'
      />
    </Box>
  );
};

export default TableSearchField;
